import React, { useEffect, useState, Fragment, useCallback } from "react";
import {
  Button,
  Col,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Modal,
  message,
  Spin,
  Switch,
  Dropdown,
} from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";
import HeadingComponent from "../../components/HeadingComponent";
import { Package, Packages_Price_Form } from "../../utils/constants";
import {
  addPrice,
  createBulkVouchers,
  deletePrice,
  getAllPrices,
  updatePackagePrice,
} from "./APIs/priceApi";
import CustomTable from "../../components/CustomTable";
import { getAllPackages } from "../../firebase/queries";
import CreateVouchers from "../Vouchers/CreateVouchers";

const { confirm } = Modal;

const PackagePrice = () => {
  const [prices, setPrices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isCreateVouchersModal, setIsCreateVouchersModal] = useState(false);
  const [editPrice, setEditPrice] = useState(null);

  const columns = [
    {
      title: "Package Price Name",
      dataIndex: "package_price_name",
    },
    {
      title: "Package ",
      dataIndex: "package_name",
    },
    {
      title: "Price",
      dataIndex: "regular_price",
    },

    {
      title: "Recurring Interval",
      dataIndex: "recurring_interval",
    },

    {
      title: "Stripe Product Id",
      dataIndex: "product_id",
    },
    {
      title: "Stripe Price Id",
      dataIndex: "stripe_price_id",
    },

    {
      title: "Action",
      key: "action",
      width: 80,

      render: (_, record) => {
        const items = [
          {
            key: "edit",
            label: "Edit",
            onClick: () => {
              handleEdit(record);
            },
          },
          {
            key: "create_vouchers",
            label: "Create Vouchers",
            onClick: () => {
              setEditPrice(record);
              setIsCreateVouchersModal(true);
            },
          },
        ];
        return (
          <Space size="middle">
            <Dropdown menu={{ items }} placement="bottomRight">
              <EditOutlined className="icons_style" />
            </Dropdown>
            <DeleteOutlined
              className="icons_style"
              onClick={() => showDeleteConfirm(record)}
            />
          </Space>
        );
      },
    },
  ];
  const showDeleteConfirm = ({ id }) => {
    confirm({
      title: `Are you sure delete this package?`,
      icon: <DeleteOutlined />,
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk() {
        handleDelete(id);
      },
    });
  };
  const handleEdit = (record) => {
    setEditPrice(record);
    setIsEdit(true);
  };
  const handleDelete = async (id) => {
    setLoading(true);
    await deletePrice(id).finally(() => {
      setLoading(false);
    });
    const filtered = prices.filter((x) => x.id !== id);
    setPrices(filtered);
    message.success("Price deleted!");
  };

  const getPrices = async () => {
    try {
      setLoading(true);
      let response = await getAllPrices();
      setPrices(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getPackages = async () => {
    try {
      setLoading(true);
      let response = await getAllPackages();
      setPackages(response);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getPrices();
    getPackages();
  }, []);

  const setPariceCallback = useCallback((data) => {
    setPrices((prevState) => {
      if (prevState.find((row) => row.id === data.id)) {
        const index = prevState.findIndex((row) => row.id === data.id);
        prevState.splice(index, 1, data);
      } else {
        prevState.push(data);
      }
      return [...prevState];
    });
  }, []);

  const handleCreateVouchers = async (data) => {
    setLoading(true);
    const obj = {
      ...data,
      price_id: editPrice?.stripe_price_id,
      package_price_name: editPrice?.package_price_name,
      package_id: editPrice?.package_id,
      product_id: editPrice?.product_id,
    };
    await createBulkVouchers(obj);
    message.success("Vouchers created successfully");
    setEditPrice(null);
    setLoading(false);
  };

  return (
    <Fragment>
      <div className="justify_between">
        <HeadingComponent text="Package Prices" />
        <AddEditForm
          setPrices={setPariceCallback}
          isEdit={isEdit}
          editPrice={editPrice}
          packages={packages}
          setIsEdit={setIsEdit}
        />
      </div>
      <br />

      <CustomTable dataSource={prices} columns={columns} loading={loading} />
      {isCreateVouchersModal && (
        <CreateVouchers
          loading={loading}
          open={isCreateVouchersModal}
          setOpen={setIsCreateVouchersModal}
          handleFinish={handleCreateVouchers}
        />
      )}
    </Fragment>
  );
};

export default PackagePrice;

const AddEditForm = ({
  isEdit = false,
  setIsEdit,
  setPrices,
  editPrice,
  packages,
}) => {
  const [open, setOpen] = useState(isEdit);
  const [formLoading, setFormLoading] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    if (isEdit && editPrice) {
      const safeValues = {
        package_id: editPrice.package_id || "",
        package_name: editPrice.package_name || "Default Package Name",
        package_price_name:
          editPrice?.package_price_name || "Default Package Price Name",
        regular_price: editPrice.regular_price || 0,
        sale_price: editPrice.sale_price || 0,
        price_type: editPrice.price_type || "one-time",
        recurring_interval: editPrice.recurring_interval || "monthly",
        trial_days: editPrice.trial_days ?? 0,
        stripe_price_id: editPrice.stripe_price_id || "Default Stripe Price ID",
        status: editPrice.status ?? false,
        description: editPrice.description || "",
      };
      form.setFieldsValue(safeValues);
      setOpen(true);
    }
  }, [isEdit, editPrice, form]);

  const showDrawer = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEdit(false);
    form.resetFields();
  };

  const onSubmit = () => {
    form.validateFields().then((data) => {
      isEdit ? handleUpdate(data) : handleAdd(data);
    });
  };

  const handleAdd = async (data) => {
    const obj = {
      ...data,
      description: data?.description || "",
      package_name: packages?.find((item) => item.id === data.package_id)?.name,
      product_id: packages?.find((item) => item.id === data.package_id)
        ?.product_id,
    };

    setFormLoading(true);
    const response = await addPrice(obj).finally(() => {
      setFormLoading(false);
    });
    if (response) {
      const newPackage = { ...obj, id: response };
      setPrices(newPackage);
      message.success("Package added");
      handleClose();
    }
  };

  const handleUpdate = async (data) => {
    const obj = {
      ...data,
      id: editPrice?.id,
      package_name: packages?.find((item) => item.id === data.package_id)?.name,
      product_id: packages?.find((item) => item.id === data.package_id)
        ?.product_id,
    };
    setFormLoading(true);
    const response = await updatePackagePrice(obj).finally(() => {
      setFormLoading(false);
    });

    if (response) {
      const newPackage = { ...obj, id: response };
      setPrices(newPackage);
      message.success("Package Updated");
      handleClose();
    }
  };

  return (
    <Fragment>
      <Button
        type="primary"
        onClick={showDrawer}
        icon={<PlusOutlined />}
        size="medium"
      >
        Add Package Price
      </Button>

      <Drawer
        title={isEdit ? `Edit ${Package}` : `Add ${Package}`}
        width={720}
        onClose={handleClose}
        open={open}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
      >
        <Spin spinning={formLoading}>
          <Form layout="vertical" form={form} onFinish={onSubmit}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.package_id.name}
                  label={Packages_Price_Form.package_id.label}
                  rules={Packages_Price_Form.package_id.rules}
                >
                  <Select
                    placeholder={Packages_Price_Form.package_id.placeholder}
                    options={packages?.map((item) => ({
                      label: item.name,
                      value: item.id,
                    }))}
                  />
                </Form.Item>

                <Form.Item
                  hidden
                  name={Packages_Price_Form.package_name.name}
                  label={Packages_Price_Form.package_name.label}
                >
                  <Input
                    className="full_width"
                    placeholder={Packages_Price_Form.package_name.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.package_price_name.name}
                  label={Packages_Price_Form.package_price_name.label}
                >
                  <Input
                    className="full_width"
                    placeholder={
                      Packages_Price_Form.package_price_name.placeholder
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.regular_price.name}
                  label={Packages_Price_Form.regular_price.label}
                  rules={Packages_Price_Form.regular_price.rules}
                >
                  <InputNumber
                    className="full_width"
                    min={1}
                    placeholder={Packages_Price_Form.regular_price.placeholder}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.sale_price.name}
                  label={Packages_Price_Form.sale_price.label}
                  rules={Packages_Price_Form.sale_price.rules}
                >
                  <InputNumber
                    className="full_width"
                    min={1}
                    placeholder={Packages_Price_Form.sale_price.placeholder}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.price_type.name}
                  label={Packages_Price_Form.price_type.label}
                  rules={Packages_Price_Form.price_type.rules}
                >
                  <Select
                    placeholder={Packages_Price_Form.price_type.placeholder}
                    options={[
                      { label: "Recurring", value: "recurring" },
                      { label: "One Time", value: "one-time" },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.recurring_interval.name}
                  label={Packages_Price_Form.recurring_interval.label}
                  rules={Packages_Price_Form.recurring_interval.rules}
                >
                  <Select
                    placeholder={
                      Packages_Price_Form.recurring_interval.placeholder
                    }
                    options={[
                      { label: "Monthly", value: "monthly" },
                      { label: "Yearly", value: "yearly" },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.trial_days.name}
                  label={Packages_Price_Form.trial_days.label}
                >
                  <InputNumber
                    className="full_width"
                    min={0}
                    placeholder={Packages_Price_Form.trial_days.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.stripe_price_id.name}
                  label={Packages_Price_Form.stripe_price_id.label}
                  rules={Packages_Price_Form.stripe_price_id.rules}
                >
                  <Input
                    className="full_width"
                    placeholder={
                      Packages_Price_Form.stripe_price_id.placeholder
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  name={Packages_Price_Form.status.name}
                  label={Packages_Price_Form.status.label}
                >
                  <Switch
                    defaultChecked={false}
                    placeholder={Packages_Price_Form.status.placeholder}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={Packages_Price_Form.description.name}
                  label={Packages_Price_Form.description.label}
                >
                  <Input.TextArea
                    defaultValue=""
                    placeholder={Packages_Price_Form.description.placeholder}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Flex justify="flex-end">
              <Space>
                <Button onClick={handleClose}>Cancel</Button>
                <Button htmlType="submit" type="primary">
                  Submit
                </Button>
              </Space>
            </Flex>
          </Form>
        </Spin>
      </Drawer>
    </Fragment>
  );
};
